import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentAPI, plansAPI } from '../lib/api';
import { Check, Zap, Crown, Star, MapPin, Menu, X, AlertCircle, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import PageMeta from '../components/PageMeta';

// Dynamically load Razorpay checkout script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PricingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null); // set after config loads
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [gatewayConfig, setGatewayConfig] = useState({ stripe_enabled: false, razorpay_enabled: false, razorpay_key_id: '' });
  const [configLoading, setConfigLoading] = useState(true);

  const plans = [
    {
      key: 'free',
      name: 'Free',
      price: { usd: 0, inr: 0 },
      icon: <Star className="w-6 h-6" />,
      description: 'Perfect for getting started',
      features: [
        '30 posts per month',
        '10 replies per month',
        '1 business location',
        'Manual posting only',
        'Basic profile audit',
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      key: 'basic',
      name: 'Basic',
      price: { usd: 9.99, inr: 799 },
      icon: <Zap className="w-6 h-6" />,
      description: 'Best for growing businesses',
      features: [
        '100 posts per month',
        '50 replies per month',
        '2 business locations',
        '2 team members',
        'Auto-Post & Auto-Reply',
        'Full profile audit',
        'Priority email support'
      ],
      cta: 'Upgrade Now',
      popular: true
    },
    {
      key: 'pro',
      name: 'Pro',
      price: { usd: 29.99, inr: 2499 },
      icon: <Crown className="w-6 h-6" />,
      description: 'For agencies and power users',
      features: [
        'Unlimited posts & replies',
        '5 business locations',
        '5 team members',
        'Full automation',
        'Advanced analytics',
        'Multi-location support',
        'Dedicated support'
      ],
      cta: 'Go Pro',
      popular: false
    }
  ];

  // Fetch gateway config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await paymentAPI.getConfig();
        const cfg = res.data;
        setGatewayConfig(cfg);
        // Auto-select first available method
        if (cfg.stripe_enabled) setPaymentMethod('stripe');
        else if (cfg.razorpay_enabled) setPaymentMethod('razorpay');
      } catch {
        // Fallback: assume stripe available (for .env key)
        setGatewayConfig({ stripe_enabled: true, razorpay_enabled: false, razorpay_key_id: '' });
        setPaymentMethod('stripe');
      }
      setConfigLoading(false);
    };
    fetchConfig();
  }, []);

  const handleUpgrade = async (planKey) => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (planKey === 'free' || user.membership_plan === planKey) {
      return;
    }

    if (!paymentMethod) {
      toast.error('No payment method available. Please contact support.');
      return;
    }

    setSelectedPlan(planKey);
    setLoading(true);

    try {
      // Load Razorpay script if needed
      if (paymentMethod === 'razorpay') {
        const loaded = await loadRazorpayScript();
        if (!loaded) {
          toast.error('Failed to load Razorpay. Please try again.');
          setLoading(false);
          setSelectedPlan(null);
          return;
        }
      }

      const originUrl = window.location.origin;
      const response = await paymentAPI.createCheckout(planKey, paymentMethod, originUrl);

      if (paymentMethod === 'stripe' && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (paymentMethod === 'razorpay' && response.data.order_id) {
        openRazorpayCheckout(response.data, planKey);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const openRazorpayCheckout = (data, planKey) => {
    const plan = plans.find(p => p.key === planKey);
    const options = {
      key: data.key_id,
      amount: data.amount,
      currency: data.currency,
      name: 'GMB Autopilot',
      description: `${plan.name} Plan - Monthly`,
      order_id: data.order_id,
      handler: async (response) => {
        try {
          await paymentAPI.verifyRazorpay(
            response.razorpay_order_id,
            response.razorpay_payment_id,
            response.razorpay_signature
          );
          toast.success('Payment successful! Your plan has been upgraded.');
          navigate('/dashboard');
        } catch {
          toast.error('Payment verification failed. Please contact support.');
        }
      },
      prefill: { name: user?.name, email: user?.email },
      theme: { color: '#1A73E8' },
      modal: {
        ondismiss: () => {
          setLoading(false);
          setSelectedPlan(null);
        }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
    setLoading(false);
    setSelectedPlan(null);
  };

  const bothEnabled = gatewayConfig.stripe_enabled && gatewayConfig.razorpay_enabled;
  const noneEnabled = !gatewayConfig.stripe_enabled && !gatewayConfig.razorpay_enabled;

  return (
    <div className="min-h-screen bg-white">
      <PageMeta title="Pricing - GMB Autopilot" />
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#1A73E8] rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
                GMB Autopilot
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-[#5F6368] hover:text-[#202124] font-medium">Home</Link>
              <Link to="/pricing" className="text-[#202124] font-medium">Pricing</Link>
            </nav>

            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <Link to="/dashboard" className="btn-google">Dashboard</Link>
              ) : (
                <>
                  <Link to="/login" className="text-[#5F6368] hover:text-[#202124] font-medium">Sign In</Link>
                  <Link to="/register" className="btn-google">Get Started</Link>
                </>
              )}
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#5F6368] mb-4">
              PRICING
            </p>
            <h1 className="text-4xl lg:text-5xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
              Simple, transparent pricing
            </h1>
            <p className="mt-4 text-[#5F6368] max-w-xl mx-auto">
              Choose the plan that fits your business needs. Upgrade or downgrade anytime.
            </p>

            {/* Currency Toggle - only show if both gateways enabled */}
            {!configLoading && bothEnabled && (
              <div className="mt-8 inline-flex items-center bg-[#F1F3F4] rounded-lg p-1">
                <button
                  onClick={() => setPaymentMethod('stripe')}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                    paymentMethod === 'stripe' ? 'bg-white text-[#202124] shadow-sm' : 'text-[#5F6368]'
                  }`}
                  data-testid="currency-usd"
                >
                  USD ($)
                </button>
                <button
                  onClick={() => setPaymentMethod('razorpay')}
                  className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                    paymentMethod === 'razorpay' ? 'bg-white text-[#202124] shadow-sm' : 'text-[#5F6368]'
                  }`}
                  data-testid="currency-inr"
                >
                  INR (&#8377;)
                </button>
              </div>
            )}

            {/* Single gateway badge */}
            {!configLoading && !bothEnabled && !noneEnabled && (
              <div className="mt-8 inline-flex items-center gap-2 bg-[#E8F0FE] text-[#1A73E8] px-4 py-2 rounded-lg text-sm font-medium">
                <CreditCard className="w-4 h-4" />
                {gatewayConfig.stripe_enabled ? 'Payments via Stripe (USD)' : 'Payments via Razorpay (INR)'}
              </div>
            )}

            {/* No gateway warning */}
            {!configLoading && noneEnabled && (
              <div className="mt-8 inline-flex items-center gap-2 bg-[#FEF7E0] text-[#F9AB00] px-4 py-2 rounded-lg text-sm font-medium" data-testid="no-gateway-warning">
                <AlertCircle className="w-4 h-4" />
                Payment processing is currently unavailable. Please contact support.
              </div>
            )}
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.key}
                className={`relative dashboard-card flex flex-col ${
                  plan.popular ? 'border-[#1A73E8] border-2' : ''
                }`}
                data-testid={`plan-${plan.key}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-[#1A73E8] text-white text-xs font-semibold rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  plan.key === 'free' ? 'bg-[#F1F3F4] text-[#5F6368]' :
                  plan.key === 'basic' ? 'bg-[#E8F0FE] text-[#1A73E8]' :
                  'bg-[#FEF7E0] text-[#F9AB00]'
                }`}>
                  {plan.icon}
                </div>

                <h3 className="mt-4 text-xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
                  {plan.name}
                </h3>
                <p className="text-sm text-[#5F6368]">{plan.description}</p>

                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
                    {paymentMethod === 'razorpay' ? `\u20B9${plan.price.inr}` : `$${plan.price.usd}`}
                  </span>
                  {plan.price.usd > 0 && (
                    <span className="text-[#5F6368]">/month</span>
                  )}
                </div>

                <ul className="mt-6 space-y-3 flex-1">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#202124]">
                      <Check className="w-5 h-5 text-[#1E8E3E] flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="mt-6 pt-6 border-t border-[#DADCE0]">
                  {user?.membership_plan === plan.key ? (
                    <button disabled className="w-full btn-outline opacity-50 cursor-not-allowed" data-testid={`current-${plan.key}`}>
                      Current Plan
                    </button>
                  ) : plan.key === 'free' ? (
                    <Link to={user ? "/dashboard" : "/register"} className="w-full btn-outline block text-center">
                      {user ? 'Current Plan' : plan.cta}
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleUpgrade(plan.key)}
                      disabled={(loading && selectedPlan === plan.key) || noneEnabled}
                      className={`w-full ${plan.popular ? 'btn-google' : 'btn-outline'} ${noneEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid={`upgrade-${plan.key}`}
                    >
                      {loading && selectedPlan === plan.key ? (
                        <span className="flex items-center justify-center gap-2">
                          <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                          Processing...
                        </span>
                      ) : plan.cta}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* FAQ */}
          <div className="mt-20 text-center">
            <h2 className="text-2xl font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
              Have questions?
            </h2>
            <p className="text-[#5F6368]">
              Contact us at support@gmbtools.hub
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
